'use client'

import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import {
    createTemplate,
    CreateTemplateItemData,
    Template,
    TemplateCategory,
    TemplateMedication,
    TEMPLATE_CATEGORY_LABELS,
} from '@/lib/api/templates/template-api'
import { useAuth } from '@/store/hooks'
import { useState } from 'react'
import { MdBookmarkAdd, MdClose } from 'react-icons/md'
import { toast } from 'react-toastify'

interface Props {
    isOpen: boolean
    onClose: () => void
    category: TemplateCategory
    /** Exames do pedido. Só `EXAM_REQUEST`. */
    items?: CreateTemplateItemData[]
    /** Texto do documento. Todas as categorias menos `EXAM_REQUEST`. */
    content?: string
    /**
     * Indicação clínica do pedido (`EXAM_REQUEST`) ou observações da receita
     * (`PRESCRIPTION`), aproveitadas como padrão do modelo.
     */
    notes?: string
    /** Medicamentos da receita. Só `PRESCRIPTION`. */
    medications?: TemplateMedication[]
    icdCodeId?: number | null
    /** Quem assinou o documento — vira o dono do modelo. */
    professionalId?: number | null
    /** Sugestão de nome, a partir do que o documento tem. */
    suggestedName?: string
    onSaved?: (template: Template) => void
}

/**
 * "Salvar como modelo" — transforma o documento que acabou de sair em modelo.
 *
 * O momento é este de propósito: o médico só descobre que um pedido vale virar
 * modelo DEPOIS de montá-lo pela terceira vez. Exigir que ele antecipasse isso —
 * indo a Cadastros Gerais montar o modelo antes — é o que faz recurso de modelo
 * morrer sem uso na maioria dos sistemas.
 *
 * Pede o nome porque é a única informação que o documento não tem: "Pedido #4821"
 * não ajuda ninguém a reencontrar o check-up anual seis meses depois.
 */
export default function SaveAsTemplateModal({
    isOpen,
    onClose,
    category,
    items,
    content,
    notes,
    medications,
    icdCodeId,
    professionalId,
    suggestedName,
    onSaved,
}: Props) {
    const { user, isProfessional } = useAuth()

    const [name, setName] = useState(suggestedName ?? '')
    const [owner, setOwner] = useState<number | null>(
        professionalId ??
            (isProfessional ? (user?.professionalId ?? null) : null)
    )
    const [saving, setSaving] = useState(false)

    if (!isOpen) return null

    const isExamRequest = category === TemplateCategory.EXAM_REQUEST
    const isPrescription = category === TemplateCategory.PRESCRIPTION
    const itemCount = items?.length ?? 0
    const medicationCount = medications?.length ?? 0

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()

        if (!name.trim()) {
            toast.error('Dê um nome ao modelo')
            return
        }
        if (!owner) {
            toast.error('Escolha o profissional dono do modelo')
            return
        }

        setSaving(true)
        try {
            const response = await createTemplate({
                name: name.trim(),
                category,
                professionalId: owner,
                icdCodeId: icdCodeId ?? undefined,
                ...(isExamRequest
                    ? { items: items ?? [], notes: notes?.trim() || undefined }
                    : { content: content ?? '' }),
                ...(isPrescription
                    ? {
                          medications: medications ?? [],
                          notes: notes?.trim() || undefined,
                      }
                    : {}),
            })

            if (response.success && response.result) {
                toast.success(
                    'Modelo salvo. Ele já aparece no próximo documento.'
                )
                onSaved?.(response.result)
                onClose()
            } else {
                toast.error(response.error || 'Erro ao salvar o modelo')
            }
        } catch (error) {
            toast.error('Erro ao salvar o modelo')
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box !max-w-lg">
                <div className="flex items-center justify-between mb-5">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <MdBookmarkAdd className="text-xl text-primary" />
                        Salvar como modelo
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square"
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <p className="text-sm text-base-content/60 mb-5">
                    {isExamRequest
                        ? `Guarda ${itemCount === 1 ? 'este exame' : `estes ${itemCount} exames`} para você marcar tudo num clique no próximo paciente.`
                        : isPrescription
                          ? `Guarda o texto e ${medicationCount === 1 ? 'o medicamento' : `os ${medicationCount} medicamentos`} desta receita para o próximo paciente.`
                          : 'Guarda este texto para reaproveitar no próximo paciente. As variáveis continuam sendo trocadas na emissão.'}
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="form-control w-full">
                        <label className="label pb-1">
                            <span className="label-text font-medium">
                                Nome do modelo{' '}
                                <span className="text-error">*</span>
                            </span>
                        </label>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder={
                                isExamRequest
                                    ? 'Ex.: Check-up anual'
                                    : isPrescription
                                      ? 'Ex.: Antibiótico — amigdalite'
                                      : 'Ex.: Relatório pós-operatório'
                            }
                            value={name}
                            onChange={event => setName(event.target.value)}
                            autoFocus
                            required
                        />
                        <span className="text-xs text-base-content/50 mt-1">
                            É por este nome que você vai reencontrá-lo.
                        </span>
                    </div>

                    {/* Some para o médico logado: o modelo é dele, e um seletor de
                        uma opção só atrasaria o que deve ser um clique. */}
                    {!isProfessional && (
                        <div className="form-control w-full">
                            <label className="label pb-1">
                                <span className="label-text font-medium">
                                    Profissional{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <ProfessionalSearchSelector
                                value={owner}
                                onChange={id => setOwner(id)}
                                label=""
                                placeholder="De quem é este modelo..."
                            />
                        </div>
                    )}

                    <div className="bg-base-200/50 rounded-lg p-3 text-sm text-base-content/70">
                        <span className="font-medium">
                            {TEMPLATE_CATEGORY_LABELS[category]}
                        </span>
                        {isExamRequest && (
                            <>
                                {' · '}
                                {itemCount}{' '}
                                {itemCount === 1 ? 'exame' : 'exames'}
                                {notes?.trim()
                                    ? ' · com indicação clínica'
                                    : ''}
                            </>
                        )}
                        {isPrescription && (
                            <>
                                {' · '}
                                {medicationCount}{' '}
                                {medicationCount === 1
                                    ? 'medicamento'
                                    : 'medicamentos'}
                                {notes?.trim() ? ' · com observações' : ''}
                            </>
                        )}
                    </div>

                    <div className="modal-action">
                        <button
                            type="button"
                            className="btn"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Agora não
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                        >
                            {saving ? 'Salvando...' : 'Salvar modelo'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    )
}
